<script setup>
import Footer from './FooterSection.vue'
import Breadcrumbs from './BreadCrumbs.vue'
import BcsDynamicText from './BcsDynamicText.vue'
import { useAuthStore } from '../stores/AuthStore'
import { PERMISSION_KEYS } from '../access/permissionKeys'
import glossaryIcon from '@/assets/images/dictionary.svg'
import faqsIcon from '@/assets/images/help-1.svg'
import contentLibraryIcon from '@/assets/images/bcs-content-library.svg'
import { inject, onMounted, ref, computed } from 'vue'
import {
  getDatabase,
  ref as dbRef,
  get as dbGet,
} from 'firebase/database'
const customNavigation = inject('customNavigation')
import { getApp } from 'firebase/app'
import { handleErrors } from '../utils/errorHandler'


const db = getDatabase(getApp())
const helpAndResourcesIntro = ref()
const emitter = inject('emitter')
const authStore = useAuthStore()
const hasTransformAccess = computed(() => authStore.hasPermission(PERMISSION_KEYS.TRANSFORM_ACCESS))


onMounted(async () => {
  await getHelpResourcesIntro()
})

const getHelpResourcesIntro = async () => {
  try {
    const helpRef = dbRef(db, 'BCS/Home/Help and Resources/helpResourcesPageIntro/default_value')
    const snapshot = await dbGet(helpRef)
    if (snapshot.exists()) {
      helpAndResourcesIntro.value = snapshot.val()
    }
  } catch (err) {
    handleErrors('Error fetching help and resources page intro', err)
  }
}


const handleDynamicClick = (event) => {
  if (event.target.classList.contains('contact-us')) {
    emitter.emit('toggleContactModal')
  }
}

</script>

<template>
  <div class="mt-4 help-resources-container bcs-page-padding">
    <div class="row">
      <div class="col-6">
        <Breadcrumbs></Breadcrumbs>
        <p class="help-resources-title text-28">Welcome to our Help & Resources Center</p>
      </div>
    </div>

    <div class="row">
      <div class="col-7">

        <p class="subtitle-help-resources" v-html="helpAndResourcesIntro" @click="handleDynamicClick">
        </p>


        <!-- Row 1 -->
        <div class="d-flex boxes__container">
          <div class="boxes__container-help-resources d-flex align-items-center flex-column">
            <div class="icon-background d-flex justify-content-center align-items-center">
              <img :src="faqsIcon" class="icon-help1" />
            </div>

            <h5>Frequently Asked Questions</h5>
            <p>
              <BcsDynamicText text="helpResourcesCardFaqsIntro" />
            </p>

            <button @click="
              customNavigation.navigate({
                newPage: customNavigation.routes.frequentlyQuestions.name,
                saveEvent: true
              })
              " class="btn btn-primary">
              Go to FAQs
            </button>
          </div>

          <div class="boxes__container-help-resources d-flex align-items-center flex-column">
            <div class="icon-background d-flex justify-content-center align-items-center">
              <img :src="contentLibraryIcon" class="icon-content-library" />
            </div>

            <h5>BCS Content Library</h5>
            <p>
              <BcsDynamicText text="helpResourcesCardContentLibraryIntro" />
            </p>
            <button class="btn btn-primary" @click="
              customNavigation.navigate({
                newPage: customNavigation.routes.contentLibrary.name,
                saveEvent: true
              })
              ">
              Go to BCS Content Library
            </button>
          </div>
        </div>

        <!-- Row 2 -->
        <div class="d-flex boxes__container boxes__container--second-row">
          <div v-if="hasTransformAccess" class="boxes__container-help-resources d-flex align-items-center flex-column">
            <div class="icon-background d-flex justify-content-center align-items-center">
              <img :src="contentLibraryIcon" class="icon-content-library" alt="" />
            </div>

            <h5>BCS Transform Training & Certification</h5>
            <p>
              <BcsDynamicText text="helpResourcesCardTransformIntro" />
            </p>
            <button
              class="btn btn-primary"
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.routes.transformLearningDashboard.name
                })
              "
              aria-label="Go to BCS Transform Learning Dashboard"
            >
              Go to BCS Transform
            </button>
          </div>

          <div class="boxes__container-help-resources d-flex align-items-center flex-column">
            <div class="icon-background d-flex justify-content-center align-items-center">
              <img :src="glossaryIcon" class="icon-dictionary" />
            </div>

            <h5>Glossary of Terms</h5>
            <p>
              <BcsDynamicText text="helpResourcesCardGlosaryIntro" />
            </p>
            <button class="btn btn-primary" @click="
              customNavigation.navigate({
                newPage: customNavigation.routes.glossaryTerms.name,
                saveEvent: true
              })
              ">
              Go to Glossary
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Footer></Footer>
</template>

<style scoped lang="scss">
:deep .contact-us {
  text-decoration: underline;
  cursor: pointer;
}

.boxes__container {
  margin-top: 40px;
  gap: 25px;
  height: auto;

  &--second-row {
    margin-top: 21px;
  }

  &-help-resources {
    border: 1px solid var(--bs-gray-300);
    width: calc(50% - 13px);
    border-radius: 4px;
    text-align: center;

    padding: 20px 19px; // Increased vertical padding by 2px (19px + 1px top and bottom)

    h5,
    p {
      margin: 0;
    }

    h5 {
      font-size: 20px;
      font-weight: 700;
      margin-top: 5px;
    }

    button {
      margin-top: 16px;
      height: 34px; // Decreased from 28px to reduce internal spacing
      // width: 215px; // Removed fixed width
      display: flex;
      font-size: 16px; // Increased from 14px to 16px
      font-weight: normal; // Remove bold styling
      align-items: center;
      justify-content: center;
      padding: 0 12px; // Add horizontal padding for better appearance
    }

    p {
      font-size: 14px;
      margin-top: 5px;
    }
  }
}

.help-resources-title {
  margin-top: 54px;
}

.subtitle-help-resources {
  font-size: 14px;
  margin: 0;
}

.col-container-help {
  width: 779px;
}

.icon-background {
  background-color: var(--bs-gray-200);
  width: 59px;
  height: 59px;
  border-radius: 50%;
  // margin-top: 19px;

  // .icon-help1{
  //     margin-top: 19px;
  // }
}

// Move title, subtitle and content boxes up and right
.help-resources-title {
  margin-top: 17px !important; // 54px - 36px = 18px
  margin-left: 9px !important;
  font-size: 27px !important; // Reduce from 28px to 26px
}

// Make the container wider for subtitle and content (only in HelpAndResources)
.help-resources-container .col-7 {
  max-width: 88% !important; // Increase from col-7 (58.33%) to 90%
  flex: 0 0 90% !important;
}

.subtitle-help-resources {
  margin-left: 8px !important; // 6px + 2px = 8px
  margin-top: -10px !important; // Move up 8px
  max-width: 1300px !important; // Increase width limit
  width: 100% !important; // Take full available width
  line-height: 1.4 !important; // Increase line spacing for better readability
  font-weight: normal !important; // Ensure subtitle is not bold
}

.boxes__container {
  margin-top: 38px !important; // 4px + 14px = 18px to add space between subtitle and boxes
  margin-left: 8px !important;
  
  &--second-row {
    margin-top: 26px !important;
    gap: 25px;
    margin-left: 6px !important;
  }
}
</style>
